import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { newsList, newsCategories, NewsCategory } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { paginate } from "@/utils/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const pageSize = 4;

export default function News() {
  const [category, setCategory] = useState<NewsCategory | "全部">("全部");
  const [page, setPage] = useState(1);

  useDocumentMeta(
    "影视资讯 - 午夜影院",
    "午夜影院影视资讯频道，聚合行业动态、影评、访谈与深度策划，每日更新值得读的影视内容。"
  );

  const filtered = useMemo(
    () =>
      [...newsList]
        .filter((item) => (category === "全部" ? true : item.category === category))
        .sort((a, b) => b.date.localeCompare(a.date)),
    [category]
  );

  const result = paginate(filtered, page, pageSize);
  const hotNews = [...newsList].slice(0, 3);

  return (
    <div className="w-full bg-background-50">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-9 md:px-6 md:py-12">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="transition-colors duration-300 hover:text-accent-700">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-600">影视资讯</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-600">
                <span className="h-[2px] w-6 bg-accent-500" />
                Newsroom
              </p>
              <h1 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-[32px]">
                影视资讯
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                行业动态、影评与访谈，编辑部每天为你整理值得读的影视内容。按分类筛选，找到你关心的那一条。
              </p>
            </div>
            <p className="font-label text-xs text-foreground-500">
              共 <span className="text-foreground-900">{filtered.length}</span> 篇内容
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-1 rounded-md border border-background-200 bg-background-50 p-1">
            {(["全部", ...newsCategories] as (NewsCategory | "全部")[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors duration-200 ${
                  category === item
                    ? "bg-primary-900 text-background-50"
                    : "text-foreground-600 hover:text-accent-700"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[1fr_300px] lg:gap-12">
        <div>
          {result.total === 0 ? (
            <EmptyState
              icon="ri-newspaper-line"
              title="该分类下暂无资讯"
              description="编辑们正在准备新的内容，你可以切换到其他分类，或回到首页看看推荐。"
              actionLabel="查看全部资讯"
              onAction={() => {
                setCategory("全部");
                setPage(1);
              }}
            />
          ) : (
            <div className="space-y-4">
              {result.items.map((item) => (
                <article
                  key={item.id}
                  className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors duration-300 hover:border-accent-300 md:p-5"
                >
                  <Link to={`/news/detail/${item.id}`} className="flex flex-col gap-4 sm:flex-row">
                    <div className="w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:w-52">
                      <div className="aspect-[16/10] w-full">
                        <img
                          src={item.cover}
                          alt={`${item.title}配图`}
                          title={`${item.title} 午夜影院影视资讯`}
                          className="h-full w-full object-cover object-center transition-transform duration-700 ease-smooth group-hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="rounded-sm bg-accent-100 px-1.5 py-0.5 text-accent-900">
                          {item.category}
                        </span>
                        <span className="text-foreground-400">{item.date}</span>
                        <span className="text-foreground-300">·</span>
                        <span className="text-foreground-400">{item.author}</span>
                      </div>
                      <h2 className="mt-2 text-base font-medium leading-snug text-foreground-950 transition-colors duration-300 group-hover:text-accent-700 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500 md:text-[13px]">
                        {item.excerpt}
                      </p>
                      <span className="mt-3 flex items-center gap-1 text-[11px] text-accent-700">
                        阅读全文
                        <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
              <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-4">
            <h2 className="font-heading text-sm font-semibold text-foreground-950">热门资讯</h2>
            <ul className="mt-3 space-y-3">
              {hotNews.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/news/detail/${item.id}`}
                    className="group flex gap-2.5 text-xs leading-relaxed text-foreground-700 transition-colors duration-300 hover:text-accent-700"
                  >
                    <span className="font-label text-secondary-700">0{index + 1}</span>
                    <span className="line-clamp-2">{item.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg bg-primary-900 p-4">
            <h2 className="font-heading text-sm font-semibold text-background-50">编辑专题</h2>
            <ul className="mt-3 space-y-2.5">
              {specials.slice(0, 3).map((special) => (
                <li key={special.id}>
                  <Link
                    to={`/special/${special.id}`}
                    className="flex items-center justify-between gap-2 text-xs text-background-300 transition-colors duration-300 hover:text-secondary-300"
                  >
                    <span className="truncate">{special.title}</span>
                    <i className="ri-arrow-right-up-line shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}